import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";
import { Divider, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTextField from "../../../components/CustomTextField";
import CustomUpload from "../../../components/CustomUpload";
import Select from "../../../components/Select/Select";
import {
  getClinicDetails,
  updateClinic,
} from "../../../redux/reducers/clinicSlice";
import {
  createApprovalRules,
  getApprovalRules,
  getBillingPreviewSettings,
  updateApprovalRules,
  updateBillingPreviewSettings,
} from "../../../redux/reducers/globalSettingsSlice";
import { showSnackBar } from "../../../redux/reducers/snackSlice";
import { getSessionData } from "../../../util/function";
import {
  patchMandateSession,
  readMandateSession,
} from "../../../util/mandateSessionStorage";
import "../BranchManagement/BranchManagement.css";
import "./GlobalSettings.css";

const DEFAULT_ROLE_OPTIONS = [
  { code: "FRONT_DESK", label: "Front Desk" },
  { code: "BILLING", label: "Billing" },
  { code: "BRANCH_MANAGER", label: "Practice Manager" },
  { code: "CLINIC_OWNER", label: "Clinic Owner" },
  { code: "ROLE_CLINICSUPERADMIN", label: "Super Admin" },
];

const INVOICE_FIELD_SECTIONS = [
  {
    id: "header",
    title: "Header",
    rows: [
      { key: "clinicLogo", label: "Clinic logo" },
      {
        key: "clinicNameAddressTax",
        label: "Clinic name, address & Tax/GSTIN",
      },
      { key: "invoiceNumberDate", label: "Invoice number & date" },
    ],
  },
  {
    id: "patientOwner",
    title: "Patient & owner",
    rows: [
      { key: "petNameSpeciesBreed", label: "Pet name, species & breed" },
      { key: "ownerName", label: "Owner name" },
      { key: "ownerAddress", label: "Owner address" },
      { key: "caseId", label: "Case ID" },
    ],
  },
  {
    id: "clinicalContext",
    title: "Clinical context",
    rows: [
      { key: "attendingVetName", label: "Attending vet name" },
      { key: "appointmentDateTime", label: "Appointment date & time" },
    ],
  },
  {
    id: "lineItems",
    title: "Line items",
    rows: [
      { key: "hsnSacCode", label: "HSN / SAC code" },
      { key: "batchNumberExpiry", label: "Batch number & expiry" },
      { key: "taxBreakup", label: "Tax breakup" },
      { key: "discountPercentCode", label: "Discount % and code" },
    ],
  },
  {
    id: "footer",
    title: "Footer",
    rows: [
      { key: "disclaimerText", label: "Disclaimer text" },
      { key: "footerContent", label: "Footer content" },
    ],
  },
];

const initialInvoiceFields = {
  clinicLogo: true,
  clinicNameAddressTax: true,
  invoiceNumberDate: true,
  petNameSpeciesBreed: true,
  ownerName: true,
  ownerAddress: true,
  caseId: true,
  attendingVetName: true,
  appointmentDateTime: true,
  hsnSacCode: true,
  batchNumberExpiry: true,
  taxBreakup: true,
  discountPercentCode: true,
  footerContent: true,
  disclaimerText: true,
};

const normalizeInvoiceFields = (payload = {}) => {
  const source =
    payload?.invoiceFields && typeof payload.invoiceFields === "object"
      ? payload.invoiceFields
      : payload?.settings && typeof payload.settings === "object"
        ? payload.settings
        : payload;
  const next = { ...initialInvoiceFields };
  Object.keys(initialInvoiceFields).forEach((key) => {
    if (source?.[key] != null) next[key] = !!source[key];
  });
  return next;
};

const ROLE_TO_USER_TYPES = {
  FRONT_DESK: ["ROLE_RECEPTION"],
  BILLING: ["ROLE_BILLING"],
  BRANCH_MANAGER: ["ROLE_CLINICADMIN"], //ROLE_CLINIC
  CLINIC_OWNER: ["ROLE_CLINIC"], //ROLE_ADMIN
  ROLE_CLINICSUPERADMIN: ["ROLE_CLINICSUPERADMIN"],
};

const RULE_TABS = [
  // { id: "discount", label: "Discounts above %", apiType: "DISCOUNT_PERCENT" },
  { id: "refundReturn", label: "Refunds/Returns", apiType: "REFUND" },
  // { id: "writeoff", label: "Write-offs", apiType: "WRITE_OFF" },
];

const TAB_API_TYPE = {
  discount: "DISCOUNT_PERCENT",
  refundReturn: "REFUND",
  writeoff: "WRITE_OFF",
};

const TAB_CATEGORY = {
  discount: "Billing",
  refundReturn: "Billing",
  writeoff: "Billing",
};

const DEFAULT_ORG_POLICY_MESSAGE =
  "Org policy: refunds above ₹50,000 always require Clinic Owner + Super Admin sign-off — not editable here.";

const makeLocalTier = (overrides = {}) => ({
  id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  serverId: null,
  min: "",
  max: "",
  approverRoles: [],
  approverUsers: [],
  sortOrder: 1,
  ruleType: "REFUND",
  isNew: true,
  ...overrides,
});

const initialApprovalTiers = {
  discount: [],
  refundReturn: [],
  writeoff: [],
};

const isAmountEmpty = (value) =>
  value === "" || value === null || value === undefined;

const parseAmountInput = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const num = Number(raw);
  return Number.isNaN(num) ? "" : num;
};

const normalizeRoleCode = (role) => {
  if (role == null) return "";
  if (typeof role === "string") return role;
  return role?.code || role?.value || role?.name || "";
};

const ALLOWED_ROLE_CODES = DEFAULT_ROLE_OPTIONS.map((role) => role.code);

const isOwnerLevelRole = (role) =>
  role === "CLINIC_OWNER" || role === "ROLE_CLINICSUPERADMIN";

const isClinicOwnerOnly = (roles = []) => {
  const list = Array.isArray(roles) ? roles : [];
  return list?.length > 0 && list?.every(isOwnerLevelRole);
};

const toRoleCodes = (roles = []) =>
  roles
    .map(normalizeRoleCode)
    .filter((code) => code && ALLOWED_ROLE_CODES.includes(code));

const mapApiTierToUi = (tiers = []) =>
  (Array.isArray(tiers) ? tiers : [])
    .map((tier, index) => {
      const serverId = tier?.tierId ?? tier?.id ?? null;
      const minRaw = tier?.minValue ?? tier?.min;
      const maxRaw = tier?.maxValue ?? tier?.max;
      return {
        id: serverId ?? `temp-${index}`,
        serverId,
        min: isAmountEmpty(minRaw) ? "" : Number(minRaw),
        max: isAmountEmpty(maxRaw) ? "" : Number(maxRaw),
        approverRoles: toRoleCodes(tier?.roles),
        approverUsers: Array.isArray(tier?.userIds)
          ? tier.userIds
              ?.map((id) => String(id))
              ?.filter((id) => id && id !== "undefined" && id !== "null")
          : [],
        sortOrder: Number(tier?.sortOrder ?? index),
        ruleType: tier?.ruleType || null,
        isNew: !serverId,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);

const getRuleConfig = (rulesByType, key) => {
  const value = rulesByType?.[key];
  if (Array.isArray(value)) {
    return { autoApproval: false, rules: value };
  }
  if (value && typeof value === "object") {
    return {
      // Default to auto-approve when API has no explicit autoApproval flag
      autoApproval: value.autoApproval == null ? true : !!value.autoApproval,
      rules: Array.isArray(value.rules) ? value.rules : [],
    };
  }
  return { autoApproval: true, rules: [] };
};

const initialAutoApproveByType = {
  discount: true,
  refundReturn: true,
  writeoff: true,
};

const getUserOptionId = (user) => {
  if (user == null) return "";
  if (typeof user === "object") {
    return String(
      user?.value ?? user?.id ?? user?.userId ?? user?.doctorId ?? "",
    );
  }
  return String(user);
};

const toUserIds = (users = []) =>
  users
    .map((user) => Number(getUserOptionId(user)))
    .filter((id) => !Number.isNaN(id));

const getUserRoleNames = (user) => {
  const types = user?.userType || user?.roles || user?.roleList || [];
  if (!Array.isArray(types)) return [];
  return types
    .map((role) => (typeof role === "string" ? role : role?.name))
    .filter(Boolean);
};

const BillingFinance = () => {
  const dispatch = useDispatch();
  const branches = useMemo(() => {
    const sessionBranches = readMandateSession()?.clinicBranches;
    return Array.isArray(sessionBranches) ? sessionBranches : [];
  }, []);
  const user = localStorage.getItem("user");
  const profile = useMemo(() => {
    try {
      return user ? JSON.parse(user) : {};
    } catch {
      return {};
    }
  }, [user]);
  const globalSettingsData =
    readMandateSession()?.dataSharing?.globalSettingsData ?? false;
  const showSharedTag =
    branches?.some((li) => li?.id === profile?.id && li?.branchNumber) &&
    (globalSettingsData === true || globalSettingsData === "true");
  const [activeRuleType, setActiveRuleType] = useState("refundReturn");
  const [approvalTiers, setApprovalTiers] = useState(initialApprovalTiers);
  const [autoApproveByType, setAutoApproveByType] = useState(
    initialAutoApproveByType,
  );
  const [orgPolicyMessage, setOrgPolicyMessage] = useState(
    DEFAULT_ORG_POLICY_MESSAGE,
  );
  const [saving, setSaving] = useState(false);
  const [tierErrors, setTierErrors] = useState({});
  const [hasExistingByType, setHasExistingByType] = useState({
    discount: false,
    refundReturn: false,
    writeoff: false,
  });
  const [invoiceFields, setInvoiceFields] = useState(initialInvoiceFields);
  const [clinicDet, setClinicDet] = useState(
    () => getSessionData()?.clinic ?? {},
  );
  const [brandingValues, setBrandingValues] = useState({
    image: { file: null, previewUrl: "" },
    footer: { file: null, previewUrl: "" },
    disclaimer: "",
  });

  const currentTiers = approvalTiers[activeRuleType] || [];
  const isDiscount = activeRuleType === "discount";
  const activeApiType = TAB_API_TYPE[activeRuleType] || "REFUND";
  const autoApprove = !!autoApproveByType[activeRuleType];
  // UI-only: switch ON = Requires approval; request still sends autoApproval
  const requiresApproval = !autoApprove;

  const syncBrandingFromClinic = useCallback((clinic = {}) => {
    setClinicDet(clinic || {});
    setBrandingValues({
      image: { file: null, previewUrl: clinic?.image ?? "" },
      footer: { file: null, previewUrl: clinic?.footer ?? "" },
      disclaimer: clinic?.disclaimer ?? "",
    });
  }, []);

  const toggleInvoiceField = (key) => {
    setInvoiceFields((prev) => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  };

  const onUploadBrandingFile = (name, e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setBrandingValues((prev) => ({
        ...prev,
        [name]: { file, previewUrl: reader.result },
      }));
    };
    reader.readAsDataURL(file);
  };

  const onDisclaimerChange = (value) => {
    setBrandingValues((prev) => ({
      ...prev,
      disclaimer: value,
    }));
  };

  const previewLineGrid = [
    "1.6fr",
    invoiceFields.hsnSacCode ? "0.7fr" : null,
    invoiceFields.batchNumberExpiry ? "0.9fr" : null,
    "0.8fr",
  ]
    .filter(Boolean)
    .join(" ");

  const loadClinicProfile = useCallback(() => {
    dispatch(getClinicDetails()).then((res) => {
      const payload = res?.payload;
      if (payload == null || payload === false) {
        syncBrandingFromClinic(getSessionData()?.clinic ?? {});
        return;
      }
      try {
        patchMandateSession({ clinic: payload });
      } catch {
        // ignore mandate patch errors
      }
      syncBrandingFromClinic(payload);
    });
  }, [dispatch, syncBrandingFromClinic]);

  const loadApprovalRules = useCallback(async () => {
    const result = await dispatch(getApprovalRules());
    if (!getApprovalRules.fulfilled.match(result)) return;

    const data = result.payload || {};
    const rules = data?.rules || {};

    const refundPolicy = (data?.orgPolicies || []).find(
      (policy) => policy?.ruleType === "REFUND",
    );
    setOrgPolicyMessage(refundPolicy?.message || DEFAULT_ORG_POLICY_MESSAGE);

    const discountConfig = getRuleConfig(rules, "DISCOUNT_PERCENT");
    const refundConfig = getRuleConfig(rules, "REFUND");
    const returnConfig = getRuleConfig(rules, "RETURN");
    const writeoffConfig = getRuleConfig(rules, "WRITE_OFF");

    setHasExistingByType({
      discount: !!rules?.DISCOUNT_PERCENT,
      refundReturn: !!rules?.REFUND || !!rules?.RETURN,
      writeoff: !!rules?.WRITE_OFF,
    });

    setAutoApproveByType({
      discount: discountConfig.autoApproval,
      refundReturn:
        !rules?.REFUND && !rules?.RETURN
          ? true
          : !!(
              (rules?.REFUND && refundConfig.autoApproval) ||
              (rules?.RETURN && returnConfig.autoApproval)
            ),
      writeoff: writeoffConfig.autoApproval,
    });

    setApprovalTiers({
      discount: mapApiTierToUi(discountConfig.rules),
      refundReturn: mapApiTierToUi([
        ...refundConfig.rules,
        ...returnConfig.rules,
      ]),
      writeoff: mapApiTierToUi(writeoffConfig.rules),
    });
  }, [dispatch]);

  const loadBillingPreviewSettings = useCallback(async () => {
    const result = await dispatch(getBillingPreviewSettings({ silent: true }));
    if (!getBillingPreviewSettings.fulfilled.match(result)) return;
    if (result?.payload) {
      setInvoiceFields(normalizeInvoiceFields(result.payload));
    }
  }, [dispatch]);

  useEffect(() => {
    loadApprovalRules();
    loadBillingPreviewSettings();
    loadClinicProfile();
  }, [loadApprovalRules, loadBillingPreviewSettings, loadClinicProfile]);

  const saveClinicBranding = async () => {
    const reqList = ["Physical", "Virtual", "Home"]?.map((term) => {
      const selectedTerm = clinicDet?.clinicTerms?.find(
        (trm) => trm?.consultationType === term,
      );
      const reqObj = {
        consultationType: term,
        paymentType: selectedTerm?.paymentType ?? "prepaid",
        termsfront: selectedTerm?.termsfront ?? "50",
        refundType: selectedTerm?.refundType ?? "Y",
        refund: selectedTerm?.refund ?? "100",
        status: selectedTerm?.status ?? "N",
      };
      if (selectedTerm?.termId) reqObj.termId = selectedTerm?.termId;
      return reqObj;
    });

    const form = new FormData();
    form.append("name", clinicDet?.name ?? "");
    form.append("email", clinicDet?.email ?? "");
    form.append("mobile", clinicDet?.mobile ?? "");
    form.append("location", clinicDet?.location ?? "");
    form.append("taxId", clinicDet?.taxId ?? "");
    form.append("pharmacyTaxId", clinicDet?.pharmacyTaxId ?? "");
    form.append("petShopTaxId", clinicDet?.petShopTaxId ?? "");
    form.append("disclaimer", brandingValues?.disclaimer ?? "");
    form.append("address", clinicDet?.address ?? "");
    form.append("pharmacyName", clinicDet?.pharmacyName ?? "");
    form.append("pharmacyAddress", clinicDet?.pharmacyAddress ?? "");
    form.append("petShopName", clinicDet?.petShopName ?? "");
    form.append("petShopAddress", clinicDet?.petShopAddress ?? "");
    form.append("termsRequest", JSON.stringify(reqList));
    form.append("billingMode", "Combined"); //clinicDet?.billingMode
    if (brandingValues?.image?.file) {
      form.append("image", brandingValues.image.file);
    }
    if (brandingValues?.footer?.file) {
      form.append("footer", brandingValues.footer.file);
    }

    const result = await dispatch(updateClinic(form));
    if (!result?.payload) return false;
    loadClinicProfile();
    return true;
  };
  const usersList = useMemo(() => {
    const list = getSessionData()?.vets ?? [];
    return (Array.isArray(list) ? list : []).filter((user) =>
      (user?.userType || []).some((role) => role?.name !== "ROLE_DOCTOR"),
    );
  }, []);

  const getUsersForRoles = useCallback(
    (roles = []) => {
      if (!roles.length) return [];
      const allowedTypeSet = new Set(
        roles.flatMap((role) => ROLE_TO_USER_TYPES[role] || [role]),
      );

      const matched = usersList.filter((user) => {
        const roleNames = getUserRoleNames(user);
        if (!roleNames.length) return false;
        return roleNames.some((name) => allowedTypeSet.has(name));
      });

      return matched
        .map((user) => {
          const id = user?.id ?? user?.userId ?? user?.doctorId;
          return {
            name:
              user?.name ||
              user?.userName ||
              user?.fullName ||
              `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
              `User ${id}`,
            value: String(id),
          };
        })
        .filter((user) => user.value && user.value !== "undefined");
    },
    [usersList],
  );

  const handleAddTier = () => {
    const tiers = approvalTiers[activeRuleType] || [];
    const lastTier = tiers[tiers.length - 1];
    if (lastTier && isAmountEmpty(lastTier.max)) {
      setTierErrors((prev) => ({
        ...prev,
        [lastTier.id]: {
          ...(prev[lastTier.id] || {}),
          max: "Max amount is required",
        },
      }));
      return;
    }
    const nextMin =
      lastTier && !isAmountEmpty(lastTier.max) ? Number(lastTier.max) + 1 : "";
    setApprovalTiers((prev) => ({
      ...prev,
      [activeRuleType]: [
        ...(prev[activeRuleType] || []),
        makeLocalTier({
          sortOrder: prev[activeRuleType]?.length || 0,
          ruleType: activeApiType,
          min: nextMin,
        }),
      ],
    }));
  };

  const handleRemoveTier = async (tierId) => {
    const tier = currentTiers.find((t) => t.id === tierId);
    if (!tier || tier.serverId) return;

    setApprovalTiers((prev) => ({
      ...prev,
      [activeRuleType]: prev[activeRuleType]
        .filter((t) => t.id !== tierId)
        .map((t, index) => ({ ...t, sortOrder: index })),
    }));
  };

  const updateTierField = (tierId, key, value) => {
    setApprovalTiers((prev) => ({
      ...prev,
      [activeRuleType]: (prev[activeRuleType] || []).map((t, index, list) => {
        if (t.id === tierId) return { ...t, [key]: value };
        if (key === "max" && list[index - 1]?.id === tierId) {
          return {
            ...t,
            min: isAmountEmpty(value) ? "" : Number(value) + 1,
          };
        }
        return t;
      }),
    }));
    setTierErrors((prev) => {
      const next = { ...prev };
      if (next[tierId]) {
        next[tierId] = { ...next[tierId], min: "", max: "" };
      }
      if (key === "max") {
        const list = approvalTiers[activeRuleType] || [];
        const index = list.findIndex((t) => t.id === tierId);
        const following = list[index + 1];
        if (following?.id && next[following.id]) {
          next[following.id] = { ...next[following.id], min: "" };
        }
      }
      return next;
    });
  };

  const toggleTierRole = (tierId, role) => {
    setApprovalTiers((prev) => ({
      ...prev,
      [activeRuleType]: prev[activeRuleType].map((t) => {
        if (t.id !== tierId) return t;
        const exists = t.approverRoles.includes(role);
        const nextRoles = exists
          ? t.approverRoles.filter((r) => r !== role)
          : [...t.approverRoles, role];
        const allowedUsers = new Set(
          getUsersForRoles(nextRoles).map((u) => String(u.value)),
        );
        return {
          ...t,
          approverRoles: nextRoles,
          approverUsers: isClinicOwnerOnly(nextRoles)
            ? []
            : (t.approverUsers || []).filter((u) =>
                allowedUsers.has(getUserOptionId(u)),
              ),
        };
      }),
    }));
  };

  const toggleAutoApprove = (checked) => {
    setAutoApproveByType((prev) => ({
      ...prev,
      [activeRuleType]: !checked,
    }));
  };

  const toTierRules = (tiers) =>
    tiers.map((tier, index) => {
      const rule = {
        minValue: Number(tier.min),
        maxValue: Number(tier.max),
        roles: toRoleCodes(tier.approverRoles),
        userIds: toUserIds(tier.approverUsers),
        sortOrder: Number(tier.sortOrder ?? index),
      };
      if (tier.serverId != null) {
        rule.tierId = Number(tier.serverId);
      }
      return rule;
    });

  const validateTiers = (tiers, label = "Rule", tabId) => {
    if (!tiers.length) {
      dispatch(
        showSnackBar({
          message: `${label}: add at least one threshold tier`,
          type: "error",
        }),
      );
      return false;
    }
    const errors = {};
    let firstMessage = "";
    for (let i = 0; i < tiers.length; i += 1) {
      const tier = tiers[i];
      const fieldErr = {};
      if (isAmountEmpty(tier.min)) {
        fieldErr.min = "Min amount is required";
      }
      if (isAmountEmpty(tier.max)) {
        fieldErr.max = "Max amount is required";
      }
      if (
        !fieldErr.min &&
        !fieldErr.max &&
        Number(tier.min) >= Number(tier.max)
      ) {
        fieldErr.min = "Must be smaller than max";
        fieldErr.max = "Must be greater than min";
      }
      if (fieldErr.min || fieldErr.max) {
        errors[tier.id] = fieldErr;
        if (!firstMessage) {
          firstMessage = fieldErr.min
            ? `${label} tier ${i + 1}: ${fieldErr.min}`
            : `${label} tier ${i + 1}: ${fieldErr.max}`;
        }
      }
      if (!tier.approverRoles?.length && !tier.approverUsers?.length) {
        if (!firstMessage) {
          firstMessage = `${label} tier ${i + 1}: select at least one role or specific person`;
        }
      }
    }
    if (Object.keys(errors).length > 0) {
      if (tabId) setActiveRuleType(tabId);
      setTierErrors(errors);
      dispatch(
        showSnackBar({
          message: firstMessage,
          type: "error",
        }),
      );
      return false;
    }
    if (firstMessage) {
      if (tabId) setActiveRuleType(tabId);
      setTierErrors({});
      dispatch(
        showSnackBar({
          message: firstMessage,
          type: "error",
        }),
      );
      return false;
    }
    return true;
  };

  const buildSavePayload = () =>
    RULE_TABS.map((tab) => {
      const autoApproval = !!autoApproveByType[tab.id];
      const tiers = approvalTiers[tab.id] || [];
      return {
        autoApproval,
        category: TAB_CATEGORY[tab.id] || "Billing",
        ruleType: tab.apiType,
        rules: autoApproval ? [] : toTierRules(tiers),
      };
    });

  const handleSave = async () => {
    for (const tab of RULE_TABS) {
      const autoApproval = !!autoApproveByType[tab.id];
      if (autoApproval) continue;
      if (!validateTiers(approvalTiers[tab.id] || [], tab.label, tab.id))
        return;
    }
    setSaving(true);
    try {
      const brandingSaved = await saveClinicBranding();
      if (!brandingSaved) return;

      const invoicePayload = { ...invoiceFields };
      const invoiceResult = await dispatch(
        updateBillingPreviewSettings(invoicePayload),
      );
      if (!updateBillingPreviewSettings.fulfilled.match(invoiceResult)) return;
      if (invoiceResult?.payload) {
        setInvoiceFields(normalizeInvoiceFields(invoiceResult.payload));
      }

      const payload = buildSavePayload();
      const hasExisting = RULE_TABS.some((tab) => !!hasExistingByType[tab.id]);
      const result = await dispatch(
        hasExisting
          ? updateApprovalRules(payload)
          : createApprovalRules(payload),
      );
      const matched = hasExisting
        ? updateApprovalRules.fulfilled.match(result)
        : createApprovalRules.fulfilled.match(result);
      if (!matched) return;

      setHasExistingByType({
        discount: true,
        refundReturn: true,
        writeoff: true,
      });
      await loadApprovalRules();
    } finally {
      setSaving(false);
    }
  };

  const renderAmountField = (
    tier,
    field,
    { disabled = false, error = false, helperText = "" } = {},
  ) => (
    <div className="bf-amount-input bf-tier-amount">
      {!isDiscount ? <span className="bf-amount-prefix">₹</span> : null}
      <CustomTextField
        type="number"
        value={isAmountEmpty(tier[field]) ? "" : tier[field]}
        handleChange={(e) =>
          updateTierField(tier.id, field, parseAmountInput(e?.target?.value))
        }
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth
      />
      {isDiscount ? <span className="bf-amount-suffix">%</span> : null}
    </div>
  );

  return (
    <div className="consultation-white-con p20">
      <div className="flex-row-between-align-center mb20 pb10 border-bottom">
        <div>
          <div className="flex-row-ali-cen">
            <Typography className="text-bold fs20 black">
              Billing & Finance
            </Typography>
            {showSharedTag ? (
              <Typography className="white-color fs12 con-fit card-top-orange-color ph10 br10 ml10">
                Organisation default
              </Typography>
            ) : null}
          </div>
          <Typography className="txt-regular fs13 card-gray-color mt4">
            Invoice fields, item naming, deposit usage, and approval rules.
          </Typography>
        </div>
        <div className="flex-row-ali-cen gap10">
          <CustomButton
            text="Save Changes"
            onClick={handleSave}
            smallBtn
            disabled={saving || showSharedTag}
            grayBtn={saving || showSharedTag}
          />
        </div>
      </div>

      <div className="bf-invoice-layout mb20">
        <div className="bf-invoice-fields">
          <Typography className="text-bold fs15 black">
            Invoice &amp; billing fields
          </Typography>
          <Typography className="txt-regular fs12 card-gray-color mt2 mb15">
            Choose what appears on the printed / emailed invoice. Preview
            updates live.
          </Typography>

          {INVOICE_FIELD_SECTIONS.map((section, sectionIdx) => (
            <div
              key={section.id}
              className={sectionIdx > 0 ? "bf-invoice-section" : ""}
            >
              <Typography className="bf-invoice-section-title font-bold">
                {section.title}
              </Typography>
              {section.rows.map((row) => (
                <div key={row.key}>
                  <div className="flex-row-between-align-center mv5">
                    <Typography className="font-medium fs12 black">
                      {row.label}
                    </Typography>
                    <CustomSwitch
                      value={!!invoiceFields?.[row.key]}
                      onChange={() => toggleInvoiceField(row.key)}
                      grayToBlue
                      disabled={showSharedTag}
                    />
                  </div>
                  {row.key === "clinicLogo" && invoiceFields.clinicLogo ? (
                    <div className="bf-branding-editor mb10">
                      <CustomUpload
                        uploadText=""
                        onUploadFile={(e) => onUploadBrandingFile("image", e)}
                        value={brandingValues?.image?.previewUrl}
                        profileImg
                        imageHeight={100}
                      />
                    </div>
                  ) : null}
                  {row.key === "footerContent" &&
                  invoiceFields.footerContent ? (
                    <div className="bf-branding-editor mb10">
                      <CustomUpload
                        uploadText=""
                        onUploadFile={(e) => onUploadBrandingFile("footer", e)}
                        value={brandingValues?.footer?.previewUrl}
                        profileImg
                        imageHeight={100}
                      />
                    </div>
                  ) : null}
                  {row.key === "disclaimerText" &&
                  invoiceFields.disclaimerText ? (
                    <div className="bf-branding-editor mb10">
                      <CustomTextField
                        label=""
                        name="disclaimer"
                        fullWidth
                        handleChange={(e) =>
                          onDisclaimerChange(e?.target?.value)
                        }
                        value={brandingValues?.disclaimer}
                        labelTop
                        multiline
                      />
                    </div>
                  ) : null}
                  <Divider />
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="bf-invoice-preview-wrap">
          <Typography className="bf-live-preview-label">
            Live preview
          </Typography>
          <div className="bf-invoice-preview">
            {(invoiceFields.clinicLogo ||
              invoiceFields.clinicNameAddressTax) && (
              <div className="bf-preview-header">
                <div className="bf-preview-clinic">
                  {invoiceFields.clinicLogo ? (
                    brandingValues?.image?.previewUrl ? (
                      <img
                        src={brandingValues.image.previewUrl}
                        alt="Clinic logo"
                        className="bf-preview-logo-img"
                      />
                    ) : (
                      <div className="bf-preview-logo">
                        {(clinicDet?.name?.[0] || "VI").toUpperCase()}
                      </div>
                    )
                  ) : null}
                  {invoiceFields.clinicNameAddressTax ? (
                    <div>
                      <Typography className="text-bold fs13 black">
                        {clinicDet?.name || "Clinic name"}
                      </Typography>
                      <Typography className="txt-regular fs11 card-gray-color">
                        {[clinicDet?.location, clinicDet?.taxId]
                          .filter(Boolean)
                          .join(" · ") || "Address · Tax/GSTIN"}
                      </Typography>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {invoiceFields.invoiceNumberDate ? (
              <Typography className="txt-regular fs12 black mb10">
                Invoice #INV-2026-00841 · 20 Aug 2026
              </Typography>
            ) : null}

            <div className="bf-preview-divider" />

            {(invoiceFields.petNameSpeciesBreed ||
              invoiceFields.ownerName ||
              invoiceFields.ownerAddress ||
              invoiceFields.caseId ||
              invoiceFields.attendingVetName ||
              invoiceFields.appointmentDateTime) && (
              <div className="bf-preview-meta mb12">
                {invoiceFields.petNameSpeciesBreed ? (
                  <Typography className="txt-regular fs12 black">
                    <span className="text-bold">Pet:</span> Bruno · Labrador
                    Retriever
                  </Typography>
                ) : null}
                {invoiceFields.ownerName ? (
                  <Typography className="txt-regular fs12 black">
                    <span className="text-bold">Owner:</span> R. Anand
                  </Typography>
                ) : null}
                {invoiceFields.ownerAddress ? (
                  <Typography className="txt-regular fs12 card-gray-color">
                    12, 3rd Cross, Anna Nagar, Chennai
                  </Typography>
                ) : null}
                {invoiceFields.caseId ? (
                  <Typography className="txt-regular fs12 black">
                    <span className="text-bold">Case ID:</span> CASE-88421
                  </Typography>
                ) : null}
                {invoiceFields.attendingVetName ? (
                  <Typography className="txt-regular fs12 black">
                    <span className="text-bold">Vet:</span> Dr. Supervet
                  </Typography>
                ) : null}
                {invoiceFields.appointmentDateTime ? (
                  <Typography className="txt-regular fs12 black">
                    <span className="text-bold">Appt:</span> 20 Aug 2026, 4:30
                    PM
                  </Typography>
                ) : null}
              </div>
            )}

            <div className="bf-preview-table mb10">
              <div
                className="bf-preview-table-head"
                style={{ gridTemplateColumns: previewLineGrid }}
              >
                <span>Item</span>
                {invoiceFields.hsnSacCode ? <span>HSN</span> : null}
                {invoiceFields.batchNumberExpiry ? <span>Batch</span> : null}
                <span>Amount</span>
              </div>
              <div
                className="bf-preview-table-row"
                style={{ gridTemplateColumns: previewLineGrid }}
              >
                <span>Amoxyclav 625 (10)</span>
                {invoiceFields.hsnSacCode ? <span>3004</span> : null}
                {invoiceFields.batchNumberExpiry ? (
                  <span>B2401 · 12/27</span>
                ) : null}
                <span>₹420.00</span>
              </div>
              <div
                className="bf-preview-table-row"
                style={{ gridTemplateColumns: previewLineGrid }}
              >
                <span>General Consultation</span>
                {invoiceFields.hsnSacCode ? <span>—</span> : null}
                {invoiceFields.batchNumberExpiry ? <span>—</span> : null}
                <span>₹350.00</span>
              </div>
            </div>

            <div className="bf-preview-totals mb12">
              {invoiceFields.discountPercentCode ? (
                <Typography className="txt-regular fs11 card-gray-color">
                  Discount (WELCOME10) −₹38.50
                </Typography>
              ) : null}
              {invoiceFields.taxBreakup ? (
                <Typography className="txt-regular fs11 card-gray-color">
                  CGST 6% + SGST 6% ₹100.10
                </Typography>
              ) : null}
              <Typography className="text-bold fs13 black">
                Total: ₹831.60
              </Typography>
            </div>
            {invoiceFields.disclaimerText &&
            brandingValues?.disclaimer?.length > 0 ? (
              <Typography className="bf-preview-disclaimer mb16">
                {brandingValues.disclaimer}
              </Typography>
            ) : null}
            {invoiceFields.footerContent &&
            brandingValues?.footer?.previewUrl ? (
              <div className="bf-preview-footer-content mb12">
                <img
                  src={brandingValues.footer.previewUrl}
                  alt="Clinic footer"
                  className="bf-preview-footer-img"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="bf-approval-card">
        <div className="flex-row-ali-cen mb3">
          <Typography className="text-bold fs15 black">
            Approval rules
          </Typography>
          {showSharedTag ? (
            <Typography className="white-color fs12 con-fit card-top-orange-color ph10 br10 ml10">
              Organisation default
            </Typography>
          ) : null}
        </div>
        <Typography className="txt-regular fs12 card-gray-color mb15">
          Set thresholds and who must sign off, per rule type. Approvers can be
          a role, a specific person, or both.
        </Typography>

        {/* <div className="p12 br8 amber-bg border-amber flex-row-ali-cen gap8 mv10 ph10 pv5">
          <LockOutlinedIcon sx={{ color: "#b45309", fontSize: 18 }} />
          <Typography className="fs12 font-semibold amber-color">
            {orgPolicyMessage}
          </Typography>
        </div> */}

        <div className="bf-rule-tabs">
          {RULE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`bf-rule-tab${
                activeRuleType === tab.id ? " bf-rule-tab-active" : ""
              }`}
              onClick={() => setActiveRuleType(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bf-approve-switch mb10">
          <Typography
            className={`txt-regular fs12 ${
              !requiresApproval ? "blue-color" : "card-gray-color"
            }`}
          >
            Auto approve
          </Typography>
          <CustomSwitch
            value={requiresApproval}
            onChange={(e) => toggleAutoApprove(e?.target?.value)}
            greenToGray
            disabled={showSharedTag}
          />
          <Typography
            className={`txt-regular fs12 ${
              requiresApproval ? "blue-color" : "card-gray-color"
            }`}
          >
            Requires approval
          </Typography>
        </div>

        {requiresApproval ? (
          <>
            {currentTiers.map((tier, idx) => {
              const showPersonSelect = !isClinicOwnerOnly(tier.approverRoles);
              const personOptions = showPersonSelect
                ? getUsersForRoles(tier.approverRoles)
                : [];
              const selectedPersons = personOptions.filter((option) =>
                (tier.approverUsers || []).some(
                  (selected) =>
                    getUserOptionId(selected) === String(option.value),
                ),
              );
              const rangeInvalid =
                !isAmountEmpty(tier.min) &&
                !isAmountEmpty(tier.max) &&
                Number(tier.min) >= Number(tier.max);
              const minError =
                tierErrors[tier.id]?.min ||
                (rangeInvalid ? "Must be smaller than max" : "");
              const maxError =
                tierErrors[tier.id]?.max ||
                (rangeInvalid ? "Must be greater than min" : "");
              return (
                <div className="bf-tier-card" key={tier.id}>
                  <div className="bf-tier-top">
                    <span className="bf-tier-badge">Tier {idx + 1}</span>

                    <div className="bf-amount-range">
                      {renderAmountField(tier, "min", {
                        disabled: idx !== 0 || showSharedTag,
                        error: !!minError,
                        helperText: minError,
                      })}
                      <ArrowForwardIcon
                        sx={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }}
                      />
                      {renderAmountField(tier, "max", {
                        disabled: showSharedTag,
                        error: !!maxError,
                        helperText: maxError,
                      })}
                    </div>

                    {!tier.serverId ? (
                      <button
                        type="button"
                        className="ct-task-remove-btn"
                        onClick={() => handleRemoveTier(tier.id)}
                        aria-label={`Remove tier ${idx + 1}`}
                        disabled={showSharedTag}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </button>
                    ) : null}
                  </div>

                  <div className="mt15">
                    <Typography className="txt-regular fs12 card-gray-color mb8">
                      By role
                    </Typography>
                    <div className="bf-chip-row">
                      {DEFAULT_ROLE_OPTIONS.map((role) => {
                        const selected = tier.approverRoles.includes(role.code);
                        return (
                          <button
                            key={role.code}
                            type="button"
                            className={`bf-role-chip${
                              selected ? " bf-role-chip-active" : ""
                            }`}
                            onClick={() => toggleTierRole(tier.id, role.code)}
                            disabled={showSharedTag}
                          >
                            <span className="bf-chip-check">
                              {selected ? "✓" : ""}
                            </span>
                            {role.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {showPersonSelect ? (
                    <div className="mt15">
                      <Typography className="txt-regular fs12 card-gray-color mb8">
                        By specific person (optional if a role is selected)
                      </Typography>
                      <div className="bf-person-select">
                        <Select
                          multiSelectSearchCheck
                          label=""
                          list={personOptions}
                          value={selectedPersons}
                          handleChange={(e) =>
                            updateTierField(
                              tier.id,
                              "approverUsers",
                              e?.target?.value || [],
                            )
                          }
                          disabled={showSharedTag || !tier.approverRoles?.length}
                        />
                      </div>
                      {!tier.approverRoles?.length ? (
                        <Typography className="txt-regular fs11 card-gray-color mt6">
                          Select at least one role to choose people.
                        </Typography>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}

            {showSharedTag ? null : (
              <button
                type="button"
                className="bf-add-tier-btn"
                onClick={handleAddTier}
              >
                <AddIcon sx={{ fontSize: 18 }} />
                Add threshold tier
              </button>
            )}
          </>
        ) : (
          <Typography className="txt-regular fs13 card-gray-color mt10">
            Auto approve is enabled — threshold tiers are not required for this
            rule type.
          </Typography>
        )}
      </div>
    </div>
  );
};

export default BillingFinance;
